import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type ExcludedCriticalOperation, ExcludedCriticalOperationForm } from './ExcludedCriticalOperationForm';

export const Route = createFileRoute(
    '/console/$orgId/configuration/compliance/mspr/critical-operations/excluded/edit/$id',
)({
    component: EditExcludedCriticalOperation,
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
});

function EditExcludedCriticalOperation() {
    const { id } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const orgId = useOrgId();
    const { mutate: updateExcludedCriticalOperation, isPending: isUpdating } =
        trpc.mspr.criticalOperation.updateExcludedCriticalOperation.useMutation();
    const { data, isPending: isLoading } = trpc.mspr.criticalOperation.getExcludedCriticalOperation.useQuery(
        {
            id,
        },
        { refetchOnWindowFocus: false },
    );

    const handleSubmit = (values: ExcludedCriticalOperation) => {
        message.loading('Updating excluded critical operation...');
        updateExcludedCriticalOperation(
            {
                id,
                set: {
                    riskSignedOffDate: values.riskSignedOffDate ?? null,
                    rationaleForExclusion: values.rationaleForExclusion ?? null,
                },
            },
            {
                onSuccess: () => {
                    message.success('Excluded critical operation updated');
                    trpcUtils.mspr.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    message.error(`Failed to update: ${error.message}`);
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <ExcludedCriticalOperationForm
                open={open}
                data={data}
                state={match({ isLoading, isUpdating })
                    .with({ isLoading: true }, () => 'loading' as const)
                    .with({ isUpdating: true }, () => 'submitting' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({
                        to: '/console/$orgId/configuration/compliance/mspr/critical-operations',
                        params: { orgId },
                    });
                }}
            />
        </PageController>
    );
}
